import { useEffect, useState } from "react";
import { api, ZEFFY } from "../../lib/api";
import { toast } from "sonner";
import { GraduationCap, ExternalLink } from "lucide-react";

export default function Scholarship() {
  const [tiers, setTiers] = useState([]);
  const [form, setForm] = useState({
    full_name: "", organization: "", email: "", phone: "",
    tier: "Future Workforce Sponsor", message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/scholarship/tiers").then((r) => setTiers(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/scholarship/sponsor", form);
      toast.success("Inquiry received. Our team will follow up shortly.");
      setForm({ ...form, full_name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="scholarships"
      data-testid="scholarship-section"
      className="relative py-24 md:py-32 scroll-mt-nav overflow-hidden"
    >
      <div
        className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full opacity-30 blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, #2F3B99, transparent 70%)" }}
      />
      <div className="max-w-[1500px] mx-auto px-6 md:px-12 relative">
        <div className="grid md:grid-cols-12 gap-12 mb-16">
          <div className="md:col-span-6">
            <p className="text-overline text-aawta-pink mb-4">SCHOLARSHIP FUND</p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-white tracking-tight leading-[1.05]">
              Invest in the<br /> <em className="italic text-aawta-pink">future workforce.</em>
            </h2>
            <p className="text-white/70 mt-6 max-w-lg leading-relaxed">
              Every scholarship dollar moves a student closer to a CDL, a leadership role,
              an industry career. Choose a tier or speak with our team about a custom legacy gift.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-px bg-white/10 border border-white/10 max-w-md">
              {[
                { k: "40+", v: "Scholarships Awarded" },
                { k: "$120K", v: "Distributed To Date" },
                { k: "12", v: "Partner Programs" },
              ].map((s) => (
                <div key={s.v} className="bg-aawta-jet p-4">
                  <p className="font-display text-2xl text-white">{s.k}</p>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-white/50 mt-1">{s.v}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="md:col-span-6 space-y-3">
            {tiers.map((t, i) => (
              <div
                key={t.name}
                data-testid={`scholarship-tier-${i}`}
                className="border border-white/10 p-6 flex items-center justify-between gap-6 group hover:border-aawta-pink/40 hover:bg-white/5 transition-all"
              >
                <div className="flex items-start gap-5">
                  <GraduationCap className="text-aawta-pink mt-1" size={22} />
                  <div>
                    <p className="font-display text-xl text-white">{t.name}</p>
                    <p className="text-sm text-white/60 mt-1">{t.supports}</p>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-display text-3xl text-white">${t.price.toLocaleString()}</p>
                  <a
                    href={ZEFFY.scholarship}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`scholarship-fund-${i}`}
                    className="text-overline text-aawta-pink flex items-center gap-1 justify-end mt-2 group-hover:underline"
                  >
                    FUND <ExternalLink size={11} />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Inquiry form */}
        <form
          onSubmit={submit}
          data-testid="scholarship-form"
          className="glass-panel p-8 md:p-10 grid md:grid-cols-12 gap-6 items-end"
        >
          <div className="md:col-span-12 mb-2">
            <p className="text-overline text-aawta-pink">CUSTOM SCHOLARSHIP INQUIRY</p>
            <h4 className="font-display text-2xl md:text-3xl text-white mt-2">Build a named scholarship with us.</h4>
          </div>
          <div className="md:col-span-6">
            <label className="label-luxe">Full Name *</label>
            <input data-testid="sch-name" required className="input-luxe" value={form.full_name} onChange={handle("full_name")} />
          </div>
          <div className="md:col-span-6">
            <label className="label-luxe">Organization</label>
            <input data-testid="sch-org" className="input-luxe" value={form.organization} onChange={handle("organization")} />
          </div>
          <div className="md:col-span-4">
            <label className="label-luxe">Email *</label>
            <input data-testid="sch-email" required type="email" className="input-luxe" value={form.email} onChange={handle("email")} />
          </div>
          <div className="md:col-span-4">
            <label className="label-luxe">Phone</label>
            <input data-testid="sch-phone" className="input-luxe" value={form.phone} onChange={handle("phone")} />
          </div>
          <div className="md:col-span-4">
            <label className="label-luxe">Tier of Interest</label>
            <select data-testid="sch-tier" className="input-luxe bg-aawta-jet" value={form.tier} onChange={handle("tier")}>
              {tiers.map((t) => <option key={t.name} value={t.name}>{t.name}</option>)}
              <option value="Custom Legacy Gift">Custom Legacy Gift</option>
            </select>
          </div>
          <div className="md:col-span-12">
            <label className="label-luxe">Message</label>
            <textarea data-testid="sch-message" rows={3} className="input-luxe resize-none" value={form.message} onChange={handle("message")} />
          </div>
          <div className="md:col-span-12">
            <button
              type="submit"
              data-testid="sch-submit"
              disabled={submitting}
              className="btn-pink disabled:opacity-50"
            >
              {submitting ? "SENDING..." : "REQUEST INFORMATION"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
